import React from 'react';
import {useSearchParams} from 'react-router-dom';
import type {FetchVacanciesQuery} from '@rocket-ui/lk-api';
import {OffersFilterDrawer} from 'features/offers/ui/OffersFilterDrawer';
import {OffersTable} from 'features/offers';
import {OffersTabs} from 'features/offers/ui/OffersTabs';
import {OffersToolbar} from 'features/offers/ui/OffersToolbar';
import {
	buildOffersSearchParams,
	createVacanciesQuery,
	parseOffersSearchParams,
	type OffersExtraFilters,
	type OffersSort,
	type OffersTab,
} from 'features/offers/model/offersFilters';
import {ProfileAlertsBanner} from 'features/profile';
import {PageTitle} from 'ui';

export function OffersPage(): React.ReactElement {
	const [searchParams, setSearchParams] = useSearchParams();
	const [filterOpen, setFilterOpen] = React.useState(false);

	const {tab, sort, extraFilters} = React.useMemo(
		() => parseOffersSearchParams(searchParams),
		[searchParams],
	);

	const updateUrlState = (next: {tab?: OffersTab; sort?: OffersSort; extraFilters?: OffersExtraFilters}) => {
		const params = buildOffersSearchParams({
			tab: next.tab ?? tab,
			sort: next.sort ?? sort,
			extraFilters: next.extraFilters ?? extraFilters,
		});
		setSearchParams(params, {replace: true});
	};

	const query = React.useMemo<FetchVacanciesQuery>(() => {
		const next = createVacanciesQuery(tab, sort);

		if (extraFilters.rewardCentsFrom != null) {
			next['filters[rewardCentsFrom]'] = extraFilters.rewardCentsFrom;
		}
		if (extraFilters.includeHidden) {
			next['filters[includeHidden]'] = true;
		}

		return next;
	}, [extraFilters.includeHidden, extraFilters.rewardCentsFrom, sort, tab]);

	return (
		<div className="flex flex-col min-w-0 max-sm:gap-5 max-sm:overflow-x-clip max-sm:pb-[max(1rem,env(safe-area-inset-bottom))]">
			<PageTitle className="mb-8 max-sm:mb-0 max-sm:text-[26px] max-sm:leading-snug">Предложения</PageTitle>
			<ProfileAlertsBanner className="mb-8 max-sm:mb-0" />
			<OffersTabs value={tab} onChange={(nextTab) => updateUrlState({tab: nextTab})} />
			<OffersToolbar
				sort={sort}
				onSortChange={(nextSort) => updateUrlState({sort: nextSort})}
				onFilterClick={() => setFilterOpen(true)}
			/>
			<OffersTable query={query} tab={tab} onShowAll={() => updateUrlState({tab: 'all'})} />
			<OffersFilterDrawer
				open={filterOpen}
				onClose={() => setFilterOpen(false)}
				value={extraFilters}
				onApply={(nextFilters) => updateUrlState({extraFilters: nextFilters})}
			/>
		</div>
	);
}
